import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { EGCheckbox } from "../../../../MainComponents/Checkbox/EGCheckbox";
import { useTranslation } from "react-i18next";
import { EGBasicSelect } from "../../../../MainComponents/Select/EGBasicSelect";
import { useReportContext } from "../../../ReportContext/useReportContext";
import EGDatePicker from "../../../../MainComponents/DatePicker/EGDatePicker";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { FilterGroup } from "./FilterGroup";
import { getUserList } from "../../../../../apis";
import EGAutocomplete from "../../../../MainComponents/Autocomplete/EGAutocomplete";
// Initial Checks
const checksSchema = {
  status: false,
  kind_of_severity: false,
  kind_of_entryways: false,
  principal_investigator: false,
  created: false,
  before_created: false,
  after_created: false,
  month_created: false,
  range_of_created: false, // Check 2 dates
  open_days_less_than: false,
  open_days_greater_than: false,
  open_days_range: false, // Check 2 numbers
  year_created: false,
};
// Initial values
const filtersSchema = {
  status: "",
  kind_of_severity: "",
  kind_of_entryways: "",
  principal_investigator: "",
  created: dayjs().toISOString(),
  before_created: dayjs().toISOString(),
  after_created: dayjs().toISOString(),
  month_created: "",
  open_days_less_than: 1,
  r_start_created: dayjs().toISOString(), // Range of created (start)
  r_end_created: dayjs().toISOString(), // Range of created (end)
  open_days_greater_than: 1,
  r_start_open_days: 1, // Open days range (start)
  r_end_open_days: 1, // Open days range (end)
  year_created: dayjs().year(),
};
// Options year created
const currentYear = dayjs().year();
const optionsYearCreated = Array.from(
  { length: currentYear - 2015 + 1 },
  (_, index) => {
    const year = currentYear - index;
    return { value: year.toString(), label: year.toString() };
  }
);

/* Basic search for general data */
export const BasicSearch = ({
  filterValues = {},
  setFilterValues,
  onValidationChange,
}) => {
  const { t } = useTranslation("report");
  const { statusOptions, severityOptions, kindOfEntryways, optionsMonth } =
    useReportContext();
  // Object with boolean flags indicating which validations to apply.
  const [checksFilter, setChecksFilter] = useState(checksSchema);
  const [userOptions, setUserOptions] = useState([]);

  // Load user list for principal investigator filter (silent fail if no access)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUserList();
        if (response?.users) {
          const options = response.users.map((user) => {
            const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
            return {
              value: user.id,
              label: fullName || user.email,
            };
          });
          setUserOptions(options);
        }
      } catch {
        // Silent fail - user doesn't have access to this endpoint
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const newChecks = { ...checksSchema };
    Object.keys(filterValues).forEach((key) => {
      if (key in checksSchema) {
        newChecks[key] = true;
      }
      if (key.startsWith("r_start_") || key.startsWith("r_end_")) {
        const baseKey = key.includes("created")
          ? "range_of_created"
          : "open_days_range";
        newChecks[baseKey] = true;
      }
    });
    setChecksFilter(newChecks);
  }, [filterValues]);

  const allKeys = Object.keys(checksSchema);
  const selectAllChecked = allKeys.every((key) => checksFilter[key]);

  const formatDateISO = (date) =>
    dayjs(date).isValid() ? dayjs(date).toISOString() : null;
  // onChange select all
  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    const newChecks = Object.fromEntries(allKeys.map((key) => [key, checked]));
    setChecksFilter(newChecks);

    setFilterValues((prev) => {
      const updated = { ...prev };
      if (checked) {
        return { ...updated, ...filtersSchema };
      } else {
        const cleared = { ...updated };
        Object.keys(filtersSchema).forEach((key) => delete cleared[key]);
        return cleared;
      }
    });
  };
  // onChange individual checkbox
  const handleCheckToggle = useCallback(
    (e) => {
      const { name, checked } = e.target;
      setChecksFilter((prev) => ({ ...prev, [name]: checked }));

      setFilterValues((prev) => {
        const updated = structuredClone(prev);
        if (checked) {
          if (name === "range_of_created") {
            updated.r_start_created = dayjs().toISOString();
            updated.r_end_created = dayjs().toISOString();
          } else if (name === "open_days_range") {
            updated.r_start_open_days = 1;
            updated.r_end_open_days = 1;
          } else {
            updated[name] = filtersSchema[name];
          }
        } else {
          if (name === "range_of_created") {
            delete updated.r_start_created;
            delete updated.r_end_created;
          } else if (name === "open_days_range") {
            delete updated.r_start_open_days;
            delete updated.r_end_open_days;
          } else {
            delete updated[name];
          }
        }
        return updated;
      });
    },
    [setChecksFilter, setFilterValues]
  );

  const handleFilterValues = useCallback(
    (e) => {
      const { name, value, type } = e.target || {};
      let finalValue = value;

      if (type === "number") {
        if (value === "") {
          finalValue = "";
        } else {
          const parsed = Number(value);
          if (parsed < 1) return;
          finalValue = parsed;
        }
      }
      setFilterValues((prev) => ({ ...prev, [name]: finalValue }));
    },
    [setFilterValues]
  );
  // Validations
  const validationResults = useMemo(() => {
    const isValidDateRange =
      !checksFilter.range_of_created ||
      !(
        dayjs(filterValues.r_start_created).isValid() &&
        dayjs(filterValues.r_end_created).isValid() &&
        dayjs(filterValues.r_end_created).isBefore(
          dayjs(filterValues.r_start_created)
        )
      );

    const isValidOpenDaysLessThan =
      !checksFilter.open_days_less_than || filterValues.open_days_less_than > 0;

    const isValidOpenDaysGreaterThan =
      !checksFilter.open_days_greater_than ||
      filterValues.open_days_greater_than > 0;

    const rStart = Number(filterValues.r_start_open_days) || 0;
    const rEnd = Number(filterValues.r_end_open_days) || 0;

    const openDaysStartHasError = checksFilter.open_days_range && rStart < 1;
    const openDaysEndHasError = checksFilter.open_days_range && rEnd < 1;
    const openDaysRangeError =
      checksFilter.open_days_range && rStart >= 1 && rEnd >= 1 && rEnd < rStart;

    const isValidOpenDaysRange =
      !checksFilter.open_days_range ||
      (!openDaysStartHasError && !openDaysEndHasError && !openDaysRangeError);

    const isValidYearCreated =
      !checksFilter.year_created ||
      (filterValues.year_created &&
        /^\d{4}$/.test(filterValues.year_created.toString()) &&
        Number(filterValues.year_created) >= 1900 &&
        Number(filterValues.year_created) <= currentYear);

    return {
      isValidDateRange,
      isValidOpenDaysLessThan,
      isValidOpenDaysGreaterThan,
      isValidOpenDaysRange,
      openDaysStartHasError,
      openDaysEndHasError,
      openDaysRangeError,
      isValidYearCreated,
      isSearchValid:
        isValidDateRange &&
        isValidOpenDaysLessThan &&
        isValidOpenDaysGreaterThan &&
        isValidOpenDaysRange &&
        !openDaysStartHasError &&
        !openDaysEndHasError &&
        isValidYearCreated,
    };
  }, [filterValues, checksFilter]);

  useEffect(() => {
    onValidationChange?.(validationResults.isSearchValid);
  }, [validationResults.isSearchValid, onValidationChange]);

  return (
    <Grid2 container sx={{ width: "100%", mt: 1, px: 0.5 }}>
      <Grid2 size={12} sx={{ opacity: selectAllChecked ? 1 : 0.8 }}>
        <Box sx={{ display: "inline-flex", maxWidth: "fit-content" }}>
          <EGCheckbox
            size="small"
            color="primary"
            name="selectAll"
            id="selectAll"
            checked={selectAllChecked}
            onChange={handleSelectAll}
            sx={{ maxWidth: "auto" }}
            label={
              selectAllChecked
                ? t("Filter.deselect_all")
                : t("Filter.select_all")
            }
          />
        </Box>
      </Grid2>
      {/* Report status */}
      <FilterGroup
        name="status"
        label={t("Filter.status")}
        checked={checksFilter.status}
        onToggle={handleCheckToggle}
      >
        <EGBasicSelect
          label={`${t("Filter.status")}:`}
          options={statusOptions}
          value={filterValues?.status || ""}
          onChange={(e) =>
            handleFilterValues({
              target: { name: "status", value: e.target.value },
            })
          }
        />
      </FilterGroup>
      {/* Kind of severity */}
      <FilterGroup
        name="kind_of_severity"
        label={t("Filter.kind_of_severity")}
        checked={checksFilter.kind_of_severity}
        onToggle={handleCheckToggle}
      >
        <EGBasicSelect
          label={`${t("Filter.kind_of_severity")}:`}
          options={severityOptions}
          value={filterValues?.kind_of_severity || ""}
          onChange={(e) =>
            handleFilterValues({
              target: {
                name: "kind_of_severity",
                value: e.target.value,
              },
            })
          }
        />
      </FilterGroup>
      {/* Kind of entryway */}
      <FilterGroup
        name="kind_of_entryways"
        label={t("Filter.kind_of_entryways")}
        checked={checksFilter.kind_of_entryways}
        onToggle={handleCheckToggle}
      >
        <EGBasicSelect
          label={`${t("Filter.kind_of_entryways")}:`}
          options={kindOfEntryways}
          value={filterValues?.kind_of_entryways || ""}
          onChange={(e) =>
            handleFilterValues({
              target: {
                name: "kind_of_entryways",
                value: e.target.value,
              },
            })
          }
        />
      </FilterGroup>
      {/* Principal investigator - only show if user has access */}
      {userOptions.length > 0 && (
        <FilterGroup
          name="principal_investigator"
          label={t("Filter.principal_investigator")}
          checked={checksFilter.principal_investigator}
          onToggle={handleCheckToggle}
        >
          <EGAutocomplete
            label={t("Filter.principal_investigator")}
            options={userOptions}
            value={userOptions.find((opt) => opt.value === filterValues?.principal_investigator) || null}
            onChange={(newValue) =>
              handleFilterValues({
                target: {
                  name: "principal_investigator",
                  value: newValue?.value || "",
                },
              })
            }
          />
        </FilterGroup>
      )}
      {/* Created date */}
      <FilterGroup
        name="created"
        label={t("Filter.date_created")}
        checked={checksFilter.created}
        onToggle={handleCheckToggle}
      >
        <EGDatePicker
          label={`${t("Filter.date_created")}:`}
          value={
            dayjs(filterValues.created).isValid()
              ? dayjs(filterValues.created)
              : null
          }
          onChange={(date) => {
            console.log(date);
            handleFilterValues({
              target: { name: "created", value: formatDateISO(date) },
            });
          }}
          maxDate={dayjs()}
        />
      </FilterGroup>
      {/* Creation date before */}
      <FilterGroup
        name="before_created"
        label={t("Filter.created_before")}
        checked={checksFilter.before_created}
        onToggle={handleCheckToggle}
      >
        <EGDatePicker
          label={`${t("Filter.created_before")}:`}
          value={
            dayjs(filterValues.before_created).isValid()
              ? dayjs(filterValues.before_created)
              : null
          }
          onChange={(date) =>
            handleFilterValues({
              target: {
                name: "before_created",
                value: formatDateISO(date),
              },
            })
          }
          maxDate={dayjs()}
        />
      </FilterGroup>
      {/* Creation date after */}
      <FilterGroup
        name="after_created"
        label={t("Filter.created_after")}
        checked={checksFilter.after_created}
        onToggle={handleCheckToggle}
      >
        <EGDatePicker
          value={
            dayjs(filterValues.after_created).isValid()
              ? dayjs(filterValues.after_created)
              : null
          }
          onChange={(date) =>
            handleFilterValues({
              target: {
                name: "after_created",
                value: formatDateISO(date),
              },
            })
          }
          label={`${t("Filter.created_after")}:`}
          maxDate={dayjs()}
        />
      </FilterGroup>
      {/* Month created */}
      <FilterGroup
        name="month_created"
        label={t("Filter.month_created")}
        checked={checksFilter.month_created}
        onToggle={handleCheckToggle}
      >
        <EGBasicSelect
          options={optionsMonth}
          value={filterValues?.month_created || ""}
          onChange={(e) =>
            handleFilterValues({
              target: {
                name: "month_created",
                value: e.target.value,
              },
            })
          }
        />
      </FilterGroup>
      {/* Year created */}
      <FilterGroup
        name="year_created"
        label={t("Filter.year_created")}
        checked={checksFilter.year_created}
        onToggle={handleCheckToggle}
      >
        <EGInput
          type="text"
          inputMode="numeric"
          name="year_created"
          label={`${t("Filter.year_created")}:`}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 4,
          }}
          value={filterValues?.year_created || ""}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d{0,4}$/.test(value)) {
              handleFilterValues(e);
            }
          }}
          isValid={
            !checksFilter.year_created ||
            (filterValues.year_created &&
              /^\d{4}$/.test(filterValues.year_created.toString()) &&
              Number(filterValues.year_created) >= 1900 &&
              Number(filterValues.year_created) <= currentYear)
          }
          helperText={t("Filter.please_enter_a_valid_year")}
        />
      </FilterGroup>
      {/* Range of created */}
      <FilterGroup
        name="range_of_created"
        label={t("Filter.range_of_created")}
        checked={checksFilter.range_of_created}
        onToggle={handleCheckToggle}
      >
        <Stack
          spacing={1}
          display="flex"
          alignItems="center"
          justifyContent="space-around"
          flexDirection="row"
          flexWrap="nowrap"
        >
          <EGDatePicker
            label={`${t("Filter.from")}:`}
            value={
              dayjs(filterValues.r_start_created).isValid()
                ? dayjs(filterValues.r_start_created)
                : null
            }
            onChange={(date) => {
              const formatted = formatDateISO(date);
              handleFilterValues({
                target: {
                  name: "r_start_created",
                  value: formatted,
                },
              });
            }}
            maxDate={
              dayjs(filterValues.r_end_created).isValid()
                ? dayjs(filterValues.r_end_created)
                : undefined
            }
          />

          <EGDatePicker
            label={`${t("Filter.to")}:`}
            value={
              dayjs(filterValues.r_end_created).isValid()
                ? dayjs(filterValues.r_end_created)
                : null
            }
            onChange={(date) => {
              const formatted = formatDateISO(date);
              handleFilterValues({
                target: {
                  name: "r_end_created",
                  value: formatted,
                },
              });
            }}
            minDate={
              dayjs(filterValues.r_start_created).isValid()
                ? dayjs(filterValues.r_start_created)
                : undefined
            }
            maxDate={dayjs()}
          />
        </Stack>

        {/* Range validation  */}
        {!validationResults.isValidDateRange &&
          checksFilter.range_of_created && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5, ml: 1 }}
              fontSize={11}
              textAlign={"center"}
            >
              {`* ${t(
                "ReportsList.end_date_cannot_be_earlier_than_start_date"
              )}`}
            </Typography>
          )}
      </FilterGroup>

      {/* Open days less than */}
      <FilterGroup
        name="open_days_less_than"
        label={t("Filter.open_days_less_than")}
        checked={checksFilter.open_days_less_than}
        onToggle={handleCheckToggle}
      >
        <EGInput
          type="number"
          name="open_days_less_than"
          label={`${t("Filter.open_days_less_than")}:`}
          InputProps={{ inputProps: { min: 1 } }}
          value={filterValues.open_days_less_than ?? 1}
          onChange={handleFilterValues}
          isValid={validationResults.isValidOpenDaysLessThan}
          helperText={t("ReportsList.must_be_at_least_1")}
        />
      </FilterGroup>
      {/* Open days created greater than */}
      <FilterGroup
        name="open_days_greater_than"
        label={t("Filter.open_days_greater_than")}
        checked={checksFilter.open_days_greater_than}
        onToggle={handleCheckToggle}
      >
        <EGInput
          type="number"
          name="open_days_greater_than"
          label={`${t("Filter.open_days_greater_than")}:`}
          InputProps={{ inputProps: { min: 1 } }}
          value={filterValues.open_days_greater_than ?? 1}
          onChange={handleFilterValues}
          isValid={validationResults.isValidOpenDaysGreaterThan > 0}
          helperText={t("ReportsList.must_be_at_least_1")}
        />
      </FilterGroup>
      {/* Open days range created */}
      <FilterGroup
        name="open_days_range"
        label={t("Filter.open_days_range")}
        checked={checksFilter.open_days_range}
        onToggle={handleCheckToggle}
      >
        <Stack
          spacing={1}
          display="flex"
          alignItems="stretch"
          justifyContent="space-around"
          flexDirection="row"
          flexWrap="nowrap"
        >
          <EGInput
            type="number"
            name="r_start_open_days"
            label={`${t("Filter.from")}:`}
            InputProps={{ inputProps: { min: 1 } }}
            value={filterValues.r_start_open_days ?? 1}
            onChange={handleFilterValues}
            isValid={!validationResults.openDaysStartHasError}
            helperText={t("ReportsList.must_be_at_least_1")}
          />
          <EGInput
            type="number"
            name="r_end_open_days"
            label={`${t("Filter.to")}:`}
            InputProps={{ inputProps: { min: 1 } }}
            value={filterValues.r_end_open_days ?? 1}
            onChange={handleFilterValues}
            isValid={!validationResults.openDaysEndHasError}
            helperText={t("ReportsList.must_be_at_least_1")}
          />
        </Stack>
        {validationResults.openDaysRangeError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, ml: 1 }}
            fontSize={11}
            textAlign={"center"}
          >
            {`* ${t("ReportsList.end_date_cannot_be_earlier_than_start_date")}`}
          </Typography>
        )}
      </FilterGroup>
    </Grid2>
  );
};
